<template>
  <main class="shift-content">
    <div class="shift-details">
      <div v-if="loading" class="loading-container">
        <app-loading />
      </div>
      <ShiftSignoff v-else-if="canBeFinalised" :shift="shift" />
      <Candidates
        @claim-selected="onSelectClaim"
        :selected-claim-id="selectedClaimId"
        v-else-if="claims.length && shift.most_relevant_claim_status === 'PENDING'"
        :shift="shift"
        :claims="claims"
      />
      <ShiftOverview v-else :shift="shift" />
    </div>
    <div v-if="selectedClaimId" class="selected-claim">
      <div class="details">
        <span class="name">{{ selectedClaim?.worker_name }}</span>
      </div>
      <div>
        <b-button @click="approveClaim" class="select-button" variant="primary">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M7.99992 2.66668C5.0544 2.66668 2.66659 5.05449 2.66659 8.00001C2.66659 10.9455 5.0544 13.3333 7.99992 13.3333C10.9454 13.3333 13.3333 10.9455 13.3333 8.00001C13.3333 5.05449 10.9454 2.66668 7.99992 2.66668ZM1.33325 8.00001C1.33325 4.31811 4.31802 1.33334 7.99992 1.33334C11.6818 1.33334 14.6666 4.31811 14.6666 8.00001C14.6666 11.6819 11.6818 14.6667 7.99992 14.6667C4.31802 14.6667 1.33325 11.6819 1.33325 8.00001Z"
              fill="currentColor"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M11.2323 6.03983C11.4864 6.30623 11.4765 6.72823 11.2101 6.98238L7.54135 10.4824C7.28361 10.7283 6.87811 10.7281 6.62058 10.482L4.78933 8.73199C4.52314 8.47761 4.51357 8.05561 4.76794 7.78942C5.02232 7.52323 5.44432 7.51366 5.71051 7.76803L7.08156 9.07825L10.2897 6.01764C10.5561 5.76349 10.9781 5.77343 11.2323 6.03983Z"
              fill="currentColor"
            />
          </svg>
          Confirm seletion
        </b-button>
      </div>
    </div>
  </main>
</template>

<script setup>
import ShiftSignoff from '@/pages/talent-pool/shift/components/ShiftSignoff.vue';
import ShiftOverview from '@/pages/talent-pool/shift/components/ShiftOverview.vue';
import { useRoute, useRouter } from 'vue-router/composables';
import ApiClient from '@/ApiClient';
import { computed, onMounted, ref } from 'vue';
import { canFinalise } from '@/pages/talent-pool/utils/ShiftStatus';
import Candidates from '@/pages/talent-pool/shift/components/Candidates.vue';
import AppLoading from '@/components/AppLoading.vue';
import { getShiftsForTab } from '@/state/composables/useTalentPoolShifts';

const route = useRoute();
const router = useRouter();
const shiftId = route.params.shiftId;
const tab = route.params.tab;
const locationId = route.params.locationId;

const loading = ref(true);
const shift = ref(null);
const claims = ref([]);
const selectedClaimId = ref(null);

// Computed values
const canBeFinalised = computed(() => (shift?.value ? canFinalise(shift.value) : false));

const selectedClaim = computed(() => {
  if (claims.value.length > 0) {
    return claims.value.find(claim => claim.claim_id === selectedClaimId.value);
  }
  return null;
});

// Methods
const onSelectClaim = claimId => {
  if (selectedClaimId.value === claimId) {
    selectedClaimId.value = null;
  } else {
    selectedClaimId.value = claimId;
  }
};

const approveClaim = async () => {
  await ApiClient.approveTalentPoolShiftClaim(selectedClaimId.value);
  selectedClaimId.value = null;
  const shifts = await getShiftsForTab(locationId, tab, true);
  if (shifts.length > 0) {
    router.push({
      name: 'talent-pool-shift-detail',
      params: { locationId, tab, shiftId: shifts[0].shift_id },
    });
  } else {
    router.push({
      name: 'talent-pool-shift-status',
      params: { locationId, tab },
    });
  }
};

// Fetchers
const fetchShiftDetails = async () => {
  const { data } = await ApiClient.getTalentPoolShift(shiftId);
  shift.value = data;
};

const fetchShiftClaims = async () => {
  const { data } = await ApiClient.getCandidateRanking(shiftId);
  claims.value = data;
};

onMounted(async () => {
  await fetchShiftDetails();
  if (shift.value?.most_relevant_claim_status === 'PENDING') {
    await fetchShiftClaims();
  }
  loading.value = false;
});
</script>

<style scoped lang="scss">
.shift-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.shift-details {
  padding: 2rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  overflow: auto;
}

@media (max-width: 900px) {
  .shift-details {
    padding: 1rem;
  }
}

.selected-claim {
  background-color: var(--palette-color-base-white);
  padding: 1rem 1.5rem;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

.selected-claim .details {
  flex-grow: 1;
}

.selected-claim .details .name {
  font-size: 18px;
  font-weight: 500;
}

.selected-claim .select-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-container {
  padding-top: 4rem;
}
</style>
